// Schema-driven input renderer used by both CustomerDetail's edit form and
// NewBooking's step-1 / step-3 intakes. One component so type-handling stays
// consistent — text/number/textarea/select/boolean — and so adding a new
// field type later is a one-file change.

import type { IntakeField } from '../lib/tenantContext';

interface Props {
  field: IntakeField;
  value: unknown;
  onChange: (value: unknown) => void;
}

export function IntakeFieldInput({ field, value, onChange }: Props) {
  const labelEl = (
    <label className="label">
      {field.label}
      {field.required && <span className="text-rose-400 ml-1">*</span>}
    </label>
  );

  if (field.type === 'textarea') {
    return (
      <div>
        {labelEl}
        <textarea
          className="input"
          rows={3}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div>
        {labelEl}
        <select
          className="input"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— select —</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 py-1">
        <input
          type="checkbox"
          className="h-4 w-4 accent-accent"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-sm">
          {field.label}
          {field.required && <span className="text-rose-400 ml-1">*</span>}
        </span>
      </label>
    );
  }

  // text / number
  return (
    <div>
      {labelEl}
      <input
        className="input"
        type={field.type === 'number' ? 'number' : 'text'}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (field.type === 'number') {
            if (raw === '') return onChange(undefined);
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : raw);
          } else {
            onChange(raw);
          }
        }}
      />
    </div>
  );
}

/** Read-only display of one field's value — used in the customer summary. */
export function IntakeFieldDisplay({
  field,
  value,
}: {
  field: IntakeField;
  value: unknown;
}) {
  if (value === undefined || value === null || value === '') return null;
  let display: string;
  if (field.type === 'boolean') {
    display = value ? 'Yes' : 'No';
  } else {
    display = String(value);
  }
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-muted text-xs uppercase tracking-wide">
        {field.label}
      </span>
      <span className="text-text">{display}</span>
    </div>
  );
}
